import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  CheckCircle2, Clock, Hammer, MapPin, Hash,
  Loader2, User, ChevronLeft, Phone,
} from 'lucide-react'
import { supabase } from '@/config/supabase'
import useAuthStore from '@/stores/authStore'
import { ROUTES } from '@/config/routes'

const STAGES = [
  { key: 'confirmed',   label: 'Confirmed',        icon: CheckCircle2 },
  { key: 'assigned',    label: 'Worker Assigned',   icon: User        },
  { key: 'en_route',   label: 'Worker En Route',   icon: Clock       },
  { key: 'in_progress', label: 'Work In Progress',  icon: Hammer      },
]

function stageIndex(status) {
  const idx = STAGES.findIndex((s) => s.key === status)
  return idx === -1 ? 0 : idx
}

export default function TrackBooking() {
  const { id: bookingId } = useParams()
  const navigate = useNavigate()
  const profile = useAuthStore((s) => s.profile)

  const [booking, setBooking] = useState(null)
  const [worker,  setWorker]  = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!bookingId || !profile?.id) return

    async function load() {
      const { data: bk } = await supabase
        .from('bookings')
        .select('id, ref_number, status, address, worker_id, service_categories ( name )')
        .eq('id', bookingId)
        .eq('customer_id', profile.id)
        .single()

      if (!bk) { setLoading(false); return }
      setBooking(bk)

      if (bk.worker_id) {
        const { data: wp } = await supabase
          .from('profiles')
          .select('full_name, phone')
          .eq('id', bk.worker_id)
          .single()
        setWorker(wp)
      }
      setLoading(false)
    }

    load()
  }, [bookingId, profile?.id])

  if (loading) {
    return (
      <div className="min-h-dvh bg-surface-base flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-siglat-teal animate-spin" />
      </div>
    )
  }

  if (!booking) {
    return (
      <div className="min-h-dvh bg-surface-base flex items-center justify-center px-4">
        <p className="text-sm text-ink-muted">Booking not found.</p>
      </div>
    )
  }

  const currentStage = stageIndex(booking.status)

  return (
    <div className="min-h-dvh bg-surface-base">
      <div className="max-w-md mx-auto px-4 py-8">

        {/* Back */}
        <button
          onClick={() => navigate(ROUTES.BOOK_HISTORY)}
          className="flex items-center gap-1 text-sm text-ink-muted hover:text-ink-primary mb-6 -ml-1"
        >
          <ChevronLeft className="w-4 h-4" /> My Projects
        </button>

        {/* Header */}
        <div className="mb-6">
          <h1 className="font-display text-xl font-bold text-ink-primary">Job Status</h1>
          {booking.ref_number && (
            <p className="text-xs font-mono text-siglat-teal mt-0.5">{booking.ref_number}</p>
          )}
        </div>

        {/* Booking info */}
        <div className="bg-surface-raised border border-surface-border rounded-xl p-4 mb-4 flex flex-col gap-2.5">
          {booking.service_categories?.name && (
            <div className="flex items-center gap-2.5">
              <span className="text-base">🔧</span>
              <p className="text-sm text-ink-primary">{booking.service_categories.name}</p>
            </div>
          )}
          {booking.address && (
            <div className="flex items-start gap-2.5">
              <MapPin className="w-4 h-4 text-ink-muted shrink-0 mt-0.5" />
              <p className="text-sm text-ink-muted">{booking.address}</p>
            </div>
          )}
        </div>

        {/* Progress timeline */}
        <div className="bg-surface-raised border border-surface-border rounded-xl p-5 mb-4">
          <p className="text-xs font-semibold text-ink-secondary uppercase tracking-wider mb-4">Progress</p>
          <div className="flex flex-col gap-4">
            {STAGES.map((stage, idx) => {
              const done    = idx <= currentStage
              const current = idx === currentStage
              const Icon    = stage.icon
              return (
                <div key={stage.key} className="flex items-center gap-3">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 border transition-colors
                    ${done
                      ? 'bg-siglat-teal/15 border-siglat-teal/40'
                      : 'bg-surface-overlay border-surface-border'}`}
                  >
                    <Icon className={`w-3.5 h-3.5 ${done ? 'text-siglat-teal' : 'text-ink-muted'}`} />
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${done ? 'text-ink-primary' : 'text-ink-muted'}`}>
                      {stage.label}
                    </p>
                    {current && (
                      <p className="text-[10px] text-siglat-teal font-medium uppercase tracking-wide">Current status</p>
                    )}
                  </div>
                  {current && (
                    <div className="w-2 h-2 rounded-full bg-siglat-teal animate-pulse shrink-0" />
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Worker info (if assigned) */}
        {worker ? (
          <div className="bg-surface-raised border border-siglat-teal/25 rounded-xl p-4 mb-4">
            <p className="text-xs font-semibold text-ink-secondary uppercase tracking-wider mb-3">Your Worker</p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-siglat-teal/15 border border-siglat-teal/30 flex items-center justify-center shrink-0">
                <User className="w-5 h-5 text-siglat-teal" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-ink-primary">{worker.full_name}</p>
                <p className="text-xs text-ink-muted">Verified Worker · Siglat Konstract</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-surface-raised border border-surface-border rounded-xl p-4 mb-4">
            <div className="flex items-center gap-3">
              <Clock className="w-4 h-4 text-ink-muted shrink-0" />
              <p className="text-sm text-ink-muted">A worker will be assigned shortly. We'll update this page when dispatched.</p>
            </div>
          </div>
        )}

        {/* Contact */}
        <div className="flex items-center gap-2 bg-siglat-teal/5 border border-siglat-teal/20 rounded-lg px-4 py-3">
          <Phone className="w-3.5 h-3.5 text-siglat-teal shrink-0" />
          <p className="text-xs text-ink-muted">
            Questions? Call{' '}
            <a href="tel:+639456086917" className="text-siglat-teal font-semibold">+63 945 608 6916</a>
          </p>
        </div>

      </div>
    </div>
  )
}
